import { jsPDF } from "jspdf";

/**
 * Generate a printable keepsake PDF for a shadow-work chapter.
 * Editorial layout: warm cream background, Times serif, terracotta accents.
 */
export function exportChapterPdf({ section, entries }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 64;
  const contentW = pageW - margin * 2;

  const CREAM = [244, 238, 227];
  const OBSIDIAN = [30, 27, 24];
  const TERRA = [184, 90, 68];
  const MUTED = [117, 114, 110];

  const paintBackground = () => {
    doc.setFillColor(...CREAM);
    doc.rect(0, 0, pageW, pageH, "F");
  };

  paintBackground();

  // Header
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.setTextColor(...TERRA);
  doc.text("UMBRA  ·  A SHADOW JOURNAL", margin, margin);

  // Section title
  doc.setFont("times", "normal");
  doc.setTextColor(...MUTED);
  doc.setFontSize(10);
  doc.text(section.subtitle.toUpperCase(), margin, margin + 60);

  doc.setFont("times", "normal");
  doc.setTextColor(...OBSIDIAN);
  doc.setFontSize(36);
  doc.text(section.title, margin, margin + 100);

  doc.setFont("times", "italic");
  doc.setTextColor(...MUTED);
  doc.setFontSize(12);
  const descLines = doc.splitTextToSize(section.description, contentW);
  doc.text(descLines, margin, margin + 130);

  let y = margin + 130 + descLines.length * 16 + 40;

  // Divider
  doc.setDrawColor(...TERRA);
  doc.setLineWidth(0.8);
  doc.line(margin, y, margin + 60, y);
  y += 40;

  const entriesByPrompt = new Map(entries.map((e) => [e.prompt_id, e]));

  section.prompts.forEach((prompt, idx) => {
    const entry = entriesByPrompt.get(prompt.id);

    if (y > pageH - margin - 100) {
      doc.addPage();
      paintBackground();
      y = margin;
    }

    // Prompt label
    doc.setFont("times", "normal");
    doc.setTextColor(...TERRA);
    doc.setFontSize(9);
    doc.text(`PROMPT ${String(idx + 1).padStart(2, "0")}`, margin, y);
    y += 18;

    // Prompt text (serif)
    doc.setFont("times", "normal");
    doc.setTextColor(...OBSIDIAN);
    doc.setFontSize(18);
    const pLines = doc.splitTextToSize(prompt.text, contentW);
    doc.text(pLines, margin, y);
    y += pLines.length * 22 + 14;

    if (entry?.answer) {
      doc.setFont("times", "italic");
      doc.setTextColor(...OBSIDIAN);
      doc.setFontSize(12);
      const aLines = doc.splitTextToSize(entry.answer, contentW);
      // page break check
      if (y + aLines.length * 16 > pageH - margin) {
        doc.addPage();
        paintBackground();
        y = margin;
      }
      doc.text(aLines, margin, y);
      y += aLines.length * 16 + 10;

      if (entry.mood) {
        doc.setFont("times", "normal");
        doc.setTextColor(...MUTED);
        doc.setFontSize(9);
        doc.text(`Body noted: ${entry.mood.toUpperCase()}`, margin, y);
        y += 16;
      }

      if (entry.reflection) {
        if (y + 40 > pageH - margin) {
          doc.addPage();
          paintBackground();
          y = margin;
        }
        doc.setDrawColor(...TERRA);
        doc.setLineWidth(1.2);
        doc.line(margin, y + 4, margin, y + 40);
        doc.setFont("times", "normal");
        doc.setTextColor(...TERRA);
        doc.setFontSize(8);
        doc.text("THE MIRROR", margin + 12, y + 12);
        doc.setFont("times", "italic");
        doc.setTextColor(...OBSIDIAN);
        doc.setFontSize(11);
        const rLines = doc.splitTextToSize(entry.reflection, contentW - 20);
        doc.text(rLines, margin + 12, y + 30);
        y += Math.max(40, rLines.length * 14) + 24;
      }
    } else {
      doc.setFont("times", "italic");
      doc.setTextColor(...MUTED);
      doc.setFontSize(11);
      doc.text("— unspoken —", margin, y);
      y += 24;
    }

    // Divider between prompts
    doc.setDrawColor(200, 190, 175);
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageW - margin, y);
    y += 28;
  });

  // Footer on last page
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  const stamp = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  doc.text(`Kept quietly on ${stamp}`, margin, pageH - margin / 2);

  const safeTitle = section.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`umbra-${safeTitle}.pdf`);
}


/**
 * Full archive — every chapter you have entries in, one long keepsake PDF.
 */
export function exportFullArchivePdf({ sections, entries }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 64;
  const contentW = pageW - margin * 2;

  const CREAM = [244, 238, 227];
  const OBSIDIAN = [30, 27, 24];
  const TERRA = [184, 90, 68];
  const MUTED = [117, 114, 110];

  const paintBackground = () => {
    doc.setFillColor(...CREAM);
    doc.rect(0, 0, pageW, pageH, "F");
  };

  const stamp = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  // ---- Cover page ----
  paintBackground();
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.setTextColor(...TERRA);
  doc.text("UMBRA  ·  A SHADOW JOURNAL", margin, margin + 40);

  doc.setFont("times", "normal");
  doc.setTextColor(...OBSIDIAN);
  doc.setFontSize(48);
  doc.text("Your Complete Archive", margin, margin + 140);

  doc.setFont("times", "italic");
  doc.setTextColor(...MUTED);
  doc.setFontSize(14);
  doc.text(
    doc.splitTextToSize(
      "Every prompt you have answered, every reflection Claude has offered, every mood your body has noted — gathered here in one quiet keepsake.",
      contentW - 60,
    ),
    margin,
    margin + 190,
  );

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`Kept quietly on ${stamp}`, margin, pageH - margin);

  for (const section of sections) {
    const secEntries = entries.filter((e) => e.section_id === section.id);
    if (secEntries.length === 0) continue;

    doc.addPage();
    paintBackground();

    // Chapter header page
    doc.setFont("times", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...TERRA);
    doc.text(section.subtitle.toUpperCase(), margin, margin + 40);

    doc.setFont("times", "normal");
    doc.setTextColor(...OBSIDIAN);
    doc.setFontSize(40);
    doc.text(section.title, margin, margin + 100);

    doc.setFont("times", "italic");
    doc.setTextColor(...MUTED);
    doc.setFontSize(12);
    const descLines = doc.splitTextToSize(section.description, contentW);
    doc.text(descLines, margin, margin + 130);

    let y = margin + 130 + descLines.length * 16 + 40;

    doc.setDrawColor(...TERRA);
    doc.setLineWidth(0.8);
    doc.line(margin, y, margin + 60, y);
    y += 40;

    const byPrompt = new Map(secEntries.map((e) => [e.prompt_id, e]));

    section.prompts.forEach((prompt, idx) => {
      const entry = byPrompt.get(prompt.id);
      if (!entry) return;

      if (y > pageH - margin - 100) {
        doc.addPage();
        paintBackground();
        y = margin;
      }

      doc.setFont("times", "normal");
      doc.setTextColor(...TERRA);
      doc.setFontSize(9);
      doc.text(`PROMPT ${String(idx + 1).padStart(2, "0")}`, margin, y);
      y += 18;

      doc.setFont("times", "normal");
      doc.setTextColor(...OBSIDIAN);
      doc.setFontSize(18);
      const pLines = doc.splitTextToSize(prompt.text, contentW);
      doc.text(pLines, margin, y);
      y += pLines.length * 22 + 14;

      doc.setFont("times", "italic");
      doc.setTextColor(...OBSIDIAN);
      doc.setFontSize(12);
      const aLines = doc.splitTextToSize(entry.answer, contentW);
      if (y + aLines.length * 16 > pageH - margin) {
        doc.addPage();
        paintBackground();
        y = margin;
      }
      doc.text(aLines, margin, y);
      y += aLines.length * 16 + 10;

      if (entry.mood) {
        doc.setFont("times", "normal");
        doc.setTextColor(...MUTED);
        doc.setFontSize(9);
        doc.text(`Body noted: ${entry.mood.toUpperCase()}`, margin, y);
        y += 16;
      }

      if (entry.reflection) {
        if (y + 60 > pageH - margin) {
          doc.addPage();
          paintBackground();
          y = margin;
        }
        doc.setDrawColor(...TERRA);
        doc.setLineWidth(1.2);
        const rLines = doc.splitTextToSize(entry.reflection, contentW - 20);
        const blockH = Math.max(40, rLines.length * 14) + 20;
        doc.line(margin, y + 4, margin, y + blockH);
        doc.setFont("times", "normal");
        doc.setTextColor(...TERRA);
        doc.setFontSize(8);
        doc.text("THE MIRROR", margin + 12, y + 12);
        doc.setFont("times", "italic");
        doc.setTextColor(...OBSIDIAN);
        doc.setFontSize(11);
        doc.text(rLines, margin + 12, y + 30);
        y += blockH + 12;
      }

      doc.setDrawColor(200, 190, 175);
      doc.setLineWidth(0.4);
      doc.line(margin, y, pageW - margin, y);
      y += 28;
    });
  }

  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(`Kept quietly on ${stamp}`, margin, pageH - margin / 2);

  doc.save(`umbra-archive-${new Date().toISOString().slice(0, 10)}.pdf`);
}


/**
 * A single-page printable "chapter completed" badge/certificate.
 * Landscape letter, cream background, ornamental terracotta seal.
 */
export function exportChapterBadge({ section, userName, completedAt }) {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const CREAM = [244, 238, 227];
  const OBSIDIAN = [30, 27, 24];
  const TERRA = [184, 90, 68];
  const MUTED = [117, 114, 110];
  const SEAL_BG = [232, 220, 205];

  // Background
  doc.setFillColor(...CREAM);
  doc.rect(0, 0, pageW, pageH, "F");

  // Outer thin border
  doc.setDrawColor(...TERRA);
  doc.setLineWidth(0.6);
  const bMargin = 32;
  doc.rect(bMargin, bMargin, pageW - bMargin * 2, pageH - bMargin * 2);
  // Inner border
  doc.setLineWidth(0.3);
  doc.setDrawColor(...MUTED);
  doc.rect(bMargin + 8, bMargin + 8, pageW - (bMargin + 8) * 2, pageH - (bMargin + 8) * 2);

  // Top eyebrow
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.setTextColor(...TERRA);
  doc.text("UMBRA  ·  A SHADOW JOURNAL", pageW / 2, 90, { align: "center" });

  // Small terracotta line under eyebrow
  doc.setDrawColor(...TERRA);
  doc.setLineWidth(0.8);
  doc.line(pageW / 2 - 30, 100, pageW / 2 + 30, 100);

  // "Chapter Completed"
  doc.setFont("times", "italic");
  doc.setFontSize(14);
  doc.setTextColor(...MUTED);
  doc.text("This is to mark the quiet completion of", pageW / 2, 150, { align: "center" });

  // Chapter subtitle (small caps)
  doc.setFont("times", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...TERRA);
  doc.text(section.subtitle.toUpperCase(), pageW / 2, 180, { align: "center" });

  // Chapter title — hero
  doc.setFont("times", "normal");
  doc.setFontSize(46);
  doc.setTextColor(...OBSIDIAN);
  doc.text(section.title, pageW / 2, 230, { align: "center" });

  // Section description in italic
  doc.setFont("times", "italic");
  doc.setFontSize(12);
  doc.setTextColor(...MUTED);
  const descLines = doc.splitTextToSize(section.description, pageW - 200);
  doc.text(descLines, pageW / 2, 270, { align: "center" });

  // Ornamental seal (right side)
  const sealX = pageW - 165;
  const sealY = pageH - 200;
  const sealR = 62;
  doc.setFillColor(...SEAL_BG);
  doc.circle(sealX, sealY, sealR, "F");
  doc.setDrawColor(...TERRA);
  doc.setLineWidth(1.4);
  doc.circle(sealX, sealY, sealR);
  doc.setLineWidth(0.4);
  doc.circle(sealX, sealY, sealR - 6);
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...TERRA);
  doc.text("UMBRA", sealX, sealY - 14, { align: "center" });
  doc.setFont("times", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...OBSIDIAN);
  doc.text("SEAL OF", sealX, sealY + 2, { align: "center" });
  doc.text("QUIET", sealX, sealY + 16, { align: "center" });
  doc.text("COMPLETION", sealX, sealY + 30, { align: "center" });

  // Name block (left side, bottom)
  const nameY = pageH - 180;
  const nameX = 100;
  const nameW = pageW - sealX + sealR - 250; // stop before seal
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(...MUTED);
  doc.text("Completed by", nameX, nameY);

  // The actual name — flourish
  doc.setFont("times", "italic");
  doc.setFontSize(30);
  doc.setTextColor(...OBSIDIAN);
  const cleanName = (userName || "A quiet journaler").trim();
  doc.text(cleanName, nameX, nameY + 32);

  // Underline
  doc.setDrawColor(...OBSIDIAN);
  doc.setLineWidth(0.6);
  doc.line(nameX, nameY + 42, nameX + Math.min(nameW, 340), nameY + 42);

  // Date + prompt count
  doc.setFont("times", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  const stamp = new Date(completedAt || Date.now()).toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
  const promptCount = section.prompts?.length || 4;
  doc.text(
    `${promptCount} prompts spoken · Kept quietly on ${stamp}`,
    nameX,
    nameY + 62,
  );

  // Footer whisper line
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(
    "You did not disappear. You listened.",
    pageW / 2,
    pageH - 60,
    { align: "center" },
  );

  const safeTitle = section.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`umbra-badge-${safeTitle}.pdf`);
}

/**
 * Full-book keepsake PDF — every chapter, every entry, every Mirror turn,
 * laid out for a bound edition.  Uses the same brand palette as the
 * chapter export so the digital preview and physical print stay identical.
 */
export function exportKeepsakeBook({ manuscript, userName }) {
  const doc = new jsPDF({ unit: "pt", format: [432, 648] }); // 6"x9" trade paperback
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 54;
  const contentW = pageW - margin * 2;

  const CREAM = [244, 238, 227];
  const OBSIDIAN = [30, 27, 24];
  const TERRA = [184, 90, 68];
  const MUTED = [117, 114, 110];

  const paint = () => {
    doc.setFillColor(...CREAM);
    doc.rect(0, 0, pageW, pageH, "F");
  };
  const footer = (label) => {
    doc.setFont("times", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(label, pageW / 2, pageH - 30, { align: "center" });
  };

  // ---- Cover ----
  paint();
  doc.setFillColor(...OBSIDIAN);
  doc.rect(0, 0, pageW, pageH, "F");
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(...TERRA);
  doc.text("UMBRA · a shadow journal", pageW / 2, 140, { align: "center" });
  doc.setFont("times", "normal");
  doc.setFontSize(30);
  doc.setTextColor(...CREAM);
  const coverTitle = doc.splitTextToSize(
    "The year the workbook listened.",
    contentW - 20,
  );
  doc.text(coverTitle, pageW / 2, pageH / 2 - 40, { align: "center" });
  if (userName) {
    doc.setFont("times", "italic");
    doc.setFontSize(14);
    doc.setTextColor(...MUTED);
    doc.text(userName, pageW / 2, pageH / 2 + 30, { align: "center" });
  }
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...TERRA);
  doc.text(
    "Bound from the whispers of one journaler.",
    pageW / 2,
    pageH - 80,
    { align: "center" },
  );

  // ---- Chapters ----
  (manuscript.sections || []).forEach((section) => {
    doc.addPage();
    paint();
    let y = margin + 60;
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...TERRA);
    doc.text((section.subtitle || "").toUpperCase(), margin, margin + 10);
    doc.setFont("times", "normal");
    doc.setFontSize(26);
    doc.setTextColor(...OBSIDIAN);
    doc.text(section.title || section.section_id, margin, y);
    y += 40;

    (section.entries || []).forEach((entry) => {
      // Page break check
      if (y > pageH - 140) {
        footer(section.title || section.section_id);
        doc.addPage();
        paint();
        y = margin + 20;
      }

      // Prompt
      doc.setFont("times", "italic");
      doc.setFontSize(11);
      doc.setTextColor(...TERRA);
      const promptLines = doc.splitTextToSize(entry.prompt_text || "", contentW);
      doc.text(promptLines, margin, y);
      y += promptLines.length * 15 + 6;

      // Answer
      doc.setFont("times", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...OBSIDIAN);
      const answerLines = doc.splitTextToSize(entry.answer || "", contentW);
      doc.text(answerLines, margin, y);
      y += answerLines.length * 15 + 10;

      // Mirror reflection
      if (entry.reflection) {
        if (y > pageH - 120) {
          footer(section.title || section.section_id);
          doc.addPage();
          paint();
          y = margin + 20;
        }
        doc.setFont("times", "italic");
        doc.setFontSize(10);
        doc.setTextColor(...MUTED);
        doc.text("THE MIRROR", margin, y);
        y += 14;
        doc.setFont("times", "italic");
        doc.setFontSize(11);
        doc.setTextColor(...OBSIDIAN);
        const rlines = doc.splitTextToSize(entry.reflection, contentW);
        doc.text(rlines, margin, y);
        y += rlines.length * 15 + 8;
      }

      // Conversation turns (multi-turn Mirror)
      (entry.turns || []).forEach((t) => {
        if (y > pageH - 90) {
          footer(section.title || section.section_id);
          doc.addPage();
          paint();
          y = margin + 20;
        }
        const isMirror = t.role === "mirror";
        doc.setFont("times", "italic");
        doc.setFontSize(9);
        doc.setTextColor(...MUTED);
        doc.text(isMirror ? (t.closing ? "MIRROR · CLOSING" : "MIRROR") : "YOU", margin, y);
        y += 12;
        doc.setFont("times", isMirror ? "italic" : "normal");
        doc.setFontSize(11);
        doc.setTextColor(...OBSIDIAN);
        const tlines = doc.splitTextToSize(t.text || "", contentW);
        doc.text(tlines, margin, y);
        y += tlines.length * 14 + 8;
      });

      // Divider between entries
      doc.setDrawColor(...MUTED);
      doc.setLineWidth(0.3);
      doc.line(margin + 40, y + 6, pageW - margin - 40, y + 6);
      y += 24;
    });
    footer(section.title || section.section_id);
  });

  // ---- Closing spread ----
  doc.addPage();
  paint();
  doc.setFont("times", "italic");
  doc.setFontSize(12);
  doc.setTextColor(...TERRA);
  doc.text("A closing", pageW / 2, pageH / 2 - 30, { align: "center" });
  doc.setFont("times", "italic");
  doc.setFontSize(14);
  doc.setTextColor(...OBSIDIAN);
  const closing = doc.splitTextToSize(
    "The book is not the end. It is a keepsake of the listening. Return to Umbra when you are ready to keep listening.",
    contentW - 20,
  );
  doc.text(closing, pageW / 2, pageH / 2, { align: "center" });

  const safeName = (userName || "keepsake")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  doc.save(`umbra-keepsake-${safeName}.pdf`);
}
